/**
 * BulkAssignModal — assign requestors across many sites and wells at once (OPEN-053).
 *
 * The table ships EMPTY, so this is the data-entry tool, not a convenience for later tweaks:
 * a dispatcher populating a customer's forty locations does it here or not at all.
 *
 * Filter locations on the left, pick ONE requestor on the right, then act. Three gestures,
 * all funnelling into one commit path:
 *   1. tick rows → press "Requestor 1" / "Requestor 2" under the picked person
 *   2. right-click the selection → the same two actions, plus the two Clears
 *   3. drag the selection onto one of those two slot zones
 *
 * They differ only in how the `SlotChange` is produced. `bulkImpact` → `bulkConfirmation` →
 * `bulkBody` runs identically underneath, so the confirm-before-displacing rule cannot be
 * bypassed by picking a different gesture — which is the whole reason for three of them.
 *
 * ── Built for hundreds, not the handful in dev ──────────────────────────────────────────
 * Both panels are filtered and render-capped (`list-scale.ts`); neither dumps its full list.
 * The requestor side is a pick-one rail rather than a card per person, because a card wall
 * does not survive hundreds of people and — worse — a context menu listing two entries each
 * cannot be searched at all. One picked person makes the menu a constant four items.
 *
 * The location filter is SERVER-backed for wells. `GET /v1/wells/search` caps at
 * WELL_SEARCH_MAX_LIMIT, so requesting the unfiltered catalog quietly drops every well past
 * it; sending the term makes the cap apply to the filtered set, and a full page is called out
 * on screen as probably-truncated rather than presented as the whole catalog.
 *
 * "Select these N" counts the FILTERED set, never the rendered slice — otherwise capping the
 * render would silently shrink what a bulk action applies to.
 *
 * Drag uses the native HTML5 API rather than @dnd-kit (used by DispatchBoard): the drop
 * targets are a handful of zones, but the draggable side is every row in a list that can run
 * to several hundred, and dnd-kit wants a hook per node. The rows here only need a `draggable`
 * attribute.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { WELL_SEARCH_MAX_LIMIT } from '@lodex-pro/shared/wells-paging';
import { useTankBatteries, useWellSearch } from '../sites-operators/hooks';
import { useDebounced } from '../sites-operators/CatalogSearchModal';
import { capForRender, capNotice } from './list-scale';
import { Pill, subText } from '../sites-operators/ui-bits';
import { useRequestors, useAllAssignments, useBulkAssign } from './hooks';
import {
  bulkBody,
  bulkConfirmation,
  bulkImpact,
  indexAssignments,
  occupantOf,
  BULK_TARGET_CAP,
  type BulkTarget,
  type SlotChange,
  type SlotNumber,
} from './slot-edit';
import {
  buildBulkRows,
  customersOf,
  filterBulkRows,
  rowKey,
  type BulkRow,
  type TargetKind,
} from './bulk-targets';
import { ConfirmDialog } from './ConfirmDialog';
import { formatNumber } from '@lodex-pro/shared/number-format';

const controlStyle: React.CSSProperties = {
  minHeight: '34px',
  padding: '6px 8px',
  fontSize: '13px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

const slotButtonStyle: React.CSSProperties = {
  flex: 1,
  fontSize: '12px',
  fontWeight: 600,
  padding: '4px 0',
  borderRadius: 'var(--radius-pill)',
  border: '1px dashed var(--color-border-secondary)',
  background: 'none',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
};

/**
 * Replace the browser's default drag ghost with a chip matching DispatchBoard's `DragOverlay`,
 * so the two drag interactions in this app look like the same product.
 *
 * Native drag has no overlay component: `setDragImage` must point at a node that is IN the
 * document at the moment it is called, so the chip is appended off-screen and swept on the
 * next frame — by then the browser has snapshotted it.
 */
function setDragImage(e: React.DragEvent, label: string): void {
  const chip = document.createElement('div');
  chip.textContent = label;
  Object.assign(chip.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid var(--color-accent-blue)',
    backgroundColor: 'var(--color-bg-tertiary)',
    color: 'var(--color-text-primary)',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(chip);
  e.dataTransfer.setDragImage(chip, 12, 12);
  requestAnimationFrame(() => chip.remove());
}

/**
 * Where to put the context menu.
 *
 * Windows fires `contextmenu` from Shift+F10 with the pointer coordinates at (0, 0), which
 * would pin the menu to the top-left corner of the viewport instead of the row a keyboard user
 * is actually on — so fall back to the row's own rectangle. Both paths are then clamped into
 * the viewport, since a menu opened near the bottom-right would otherwise hang off the edge.
 */
export function menuPosition(
  e: React.MouseEvent,
  menuW = 210,
  menuH = 270,
): { x: number; y: number } {
  const keyboardTriggered = e.clientX === 0 && e.clientY === 0;
  const rect = e.currentTarget.getBoundingClientRect();
  const x = keyboardTriggered ? rect.left + 8 : e.clientX;
  const y = keyboardTriggered ? rect.bottom : e.clientY;
  return {
    x: Math.max(4, Math.min(x, window.innerWidth - menuW - 4)),
    y: Math.max(4, Math.min(y, window.innerHeight - menuH - 4)),
  };
}

export interface BulkAssignModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function BulkAssignModal({ open, onOpenChange }: BulkAssignModalProps): React.JSX.Element {
  const [needle, setNeedle] = React.useState('');
  const [kind, setKind] = React.useState<TargetKind>('ALL');
  const [customer, setCustomer] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  const [menu, setMenu] = React.useState<{ x: number; y: number } | null>(null);
  const [pendingChange, setPendingChange] = React.useState<SlotChange | null>(null);
  // Which person the slot buttons, the drop zones and the context menu act on. Having ONE
  // selected requestor is what keeps the right-click menu a constant four items instead of
  // two per person — a menu cannot carry a search box.
  const [activeRequestorId, setActiveRequestorId] = React.useState<string | null>(null);
  const [requestorNeedle, setRequestorNeedle] = React.useState('');

  // The location filter is SERVER-backed for wells: `GET /v1/wells/search` caps results at
  // WELL_SEARCH_MAX_LIMIT, so fetching the unfiltered catalog silently loses every well past it.
  // Sending the term makes the cap apply to the FILTERED set, which is the set the dispatcher
  // is about to select. Debounced so typing does not fire a request per keystroke.
  const debouncedNeedle = useDebounced(needle, 250);
  const { data: batteryData } = useTankBatteries();
  const { data: wellData } = useWellSearch(debouncedNeedle, undefined, true);
  const { data: requestorData } = useRequestors(true);
  const { data: assignmentData } = useAllAssignments();
  const bulk = useBulkAssign();

  const wellsTruncated = (wellData?.wells.length ?? 0) >= WELL_SEARCH_MAX_LIMIT;

  const requestors = requestorData?.requestors ?? [];
  const rows = React.useMemo(
    () => buildBulkRows(batteryData?.tankBatteries ?? [], wellData?.wells ?? []),
    [batteryData, wellData],
  );
  // Batteries arrive complete and unfiltered, wells arrive already server-filtered; running
  // the same predicate over both keeps one visible list consistent either way.
  const matching = React.useMemo(
    () => filterBulkRows(rows, { needle, kind, customer }),
    [rows, needle, kind, customer],
  );
  const cappedTargets = capForRender(matching);
  const visible = cappedTargets.shown;
  const targetNotice = capNotice(cappedTargets, 'locations');
  const index = React.useMemo(() => indexAssignments(assignmentData), [assignmentData]);
  const selectedTargets: BulkTarget[] = React.useMemo(
    () => rows.filter((r) => selected.has(rowKey(r))),
    [rows, selected],
  );

  React.useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setNeedle('');
      setKind('ALL');
      setCustomer(null);
      setMenu(null);
      setPendingChange(null);
      setActiveRequestorId(null);
      setRequestorNeedle('');
    }
  }, [open]);

  // Dismiss the context menu on any click elsewhere, the way a native one behaves.
  React.useEffect(() => {
    if (menu === null) return;
    const close = (): void => setMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return (): void => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  const toggleRow = (r: BulkRow): void => {
    const key = rowKey(r);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  /**
   * Select every row matching the filter — `matching`, NOT the render-capped `visible`.
   *
   * The cap is a rendering concern only. Acting on the visible slice would mean a dispatcher
   * who filters to a customer's 342 locations and presses "select these" silently gets 100,
   * which is the under-selection this whole change exists to prevent.
   */
  const selectAllMatching = (): void => {
    const next = new Set(selected);
    const allOn = matching.every((r) => next.has(rowKey(r)));
    for (const r of matching) {
      if (allOn) next.delete(rowKey(r));
      else next.add(rowKey(r));
    }
    setSelected(next);
  };

  const nameOf = (id: string): string | null => requestors.find((r) => r.id === id)?.name ?? null;

  // The requestor rail: filtered client-side (the active list is already loaded and small
  // enough to be instant) and render-capped the same way the location list is.
  const requestorHay = requestorNeedle.trim().toLowerCase();
  const matchingRequestors =
    requestorHay === ''
      ? requestors
      : requestors.filter(
          (r) =>
            r.name.toLowerCase().includes(requestorHay) ||
            (r.company ?? '').toLowerCase().includes(requestorHay),
        );
  const cappedRequestors = capForRender(matchingRequestors);
  const requestorNotice = capNotice(cappedRequestors, 'requestors');
  const activeRequestor = requestors.find((r) => r.id === activeRequestorId) ?? null;

  const impactOf = (change: SlotChange): ReturnType<typeof bulkImpact> =>
    bulkImpact(selectedTargets, index, change);

  const commit = (change: SlotChange): void => {
    const impact = impactOf(change);
    if (impact.changing.length === 0) {
      toast.info('Nothing to change — those locations already match.');
      setPendingChange(null);
      return;
    }
    bulk.mutate(bulkBody(impact.changing, change), {
      onSuccess: (res) => {
        // A 200 with a non-empty `failed` is the normal partial case, not an error. Reporting
        // only `updated` would hide it — which is the bug this response shape exists to stop.
        if (res.failed.length > 0) {
          toast.warning(
            `${res.updated} updated, ${res.failed.length} failed — ${res.failed[0]?.reason ?? ''}`,
          );
        } else {
          toast.success(`${res.updated} ${res.updated === 1 ? 'location' : 'locations'} updated`);
        }
        setPendingChange(null);
        setSelected(new Set());
      },
      onError: () => {
        toast.error('Could not apply the bulk change');
        setPendingChange(null);
      },
    });
  };

  /** Every gesture lands here — confirm first if anybody is displaced. */
  const request = (change: SlotChange): void => {
    setMenu(null);
    if (selectedTargets.length === 0) {
      toast.info('Select some sites or wells first.');
      return;
    }
    if (selectedTargets.length > BULK_TARGET_CAP) {
      toast.error(`Select at most ${BULK_TARGET_CAP} locations at a time.`);
      return;
    }
    const impact = impactOf(change);
    if (
      bulkConfirmation(
        impact,
        change,
        change.kind === 'set' ? nameOf(change.requestorId) : null,
      ) === null
    ) {
      commit(change);
      return;
    }
    setPendingChange(change);
  };

  const pendingPrompt =
    pendingChange === null
      ? null
      : bulkConfirmation(
          impactOf(pendingChange),
          pendingChange,
          pendingChange.kind === 'set' ? nameOf(pendingChange.requestorId) : null,
        );

  // ── Drag: the payload is the current selection, so a drag needs no separate state ──
  const onRowDragStart =
    (r: BulkRow) =>
    (e: React.DragEvent): void => {
      // Dragging an unticked row acts on that row alone — the obvious reading of the gesture.
      const dragging = selected.has(rowKey(r)) ? selectedTargets : [r];
      if (!selected.has(rowKey(r))) setSelected(new Set([rowKey(r)]));
      e.dataTransfer.effectAllowed = 'link';
      e.dataTransfer.setData('text/plain', rowKey(r));
      setDragImage(e, dragging.length === 1 ? r.label : `${dragging.length} locations`);
    };

  const dropZone = (requestorId: string, slot: SlotNumber): React.JSX.Element => (
    <button
      type="button"
      onClick={(): void => request({ kind: 'set', slot, requestorId })}
      onDragOver={(e): void => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'link';
      }}
      onDrop={(e): void => {
        e.preventDefault();
        request({ kind: 'set', slot, requestorId });
      }}
      style={slotButtonStyle}
      title={`Assign the selected locations to requestor ${slot}`}
    >
      Requestor {slot}
    </button>
  );

  const selectedCount = selectedTargets.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Assign requestors to locations</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <input
            value={needle}
            onChange={(e): void => setNeedle(e.target.value)}
            placeholder="Filter by site, well, API or customer…"
            aria-label="Filter locations"
            style={{ ...controlStyle, flex: 1, minWidth: '220px' }}
          />
          <select
            value={kind}
            onChange={(e): void => setKind(e.target.value as TargetKind)}
            aria-label="Location type"
            style={controlStyle}
          >
            <option value="ALL">Sites and wells</option>
            <option value="TANK_BATTERY">Sites only</option>
            <option value="WELL">Wells only</option>
          </select>
          <select
            value={customer ?? ''}
            onChange={(e): void => setCustomer(e.target.value === '' ? null : e.target.value)}
            aria-label="Customer"
            style={controlStyle}
          >
            <option value="">All customers</option>
            {customersOf(rows).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '14px', minHeight: 0 }}>
          {/* ── Locations ── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '6px',
              }}
            >
              <button
                type="button"
                onClick={selectAllMatching}
                style={{
                  fontSize: '12px',
                  color: 'var(--color-accent-blue)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                {/* Counts `matching`, not the rendered slice — the button does what it says
                    even when the list below is capped. */}
                {matching.length > 0 && matching.every((r) => selected.has(rowKey(r)))
                  ? 'Clear these'
                  : `Select these ${matching.length}`}
              </button>
              <span style={subText}>{formatNumber(selectedCount)} selected</span>
            </div>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '46vh',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '4px',
              }}
            >
              {/* 86bc84c5y — same contradiction as the site picker: when the page came
                  back capped, "No matches." alone is wrong, and the cap notice below is
                  suppressed while this is showing. */}
              {matching.length === 0 && (
                <p style={{ ...subText, padding: '10px' }}>
                  {wellsTruncated
                    ? `No match in the first ${WELL_SEARCH_MAX_LIMIT} wells — narrow the filter.`
                    : 'No matches.'}
                </p>
              )}
              {visible.map((r) => {
                const key = rowKey(r);
                const isOn = selected.has(key);
                const s1 = occupantOf(index, r, 1);
                const s2 = occupantOf(index, r, 2);
                return (
                  <div
                    key={key}
                    draggable
                    // Focusable so Shift+F10 can reach the context menu at all, and so the
                    // whole flow works without a pointer: Tab to a row, Space to tick it,
                    // Shift+F10 for the actions.
                    tabIndex={0}
                    role="checkbox"
                    aria-checked={isOn}
                    onKeyDown={(e): void => {
                      if (e.key !== ' ' && e.key !== 'Enter') return;
                      e.preventDefault();
                      toggleRow(r);
                    }}
                    onDragStart={onRowDragStart(r)}
                    onClick={(): void => toggleRow(r)}
                    onContextMenu={(e): void => {
                      e.preventDefault();
                      // Right-clicking an unticked row acts on that row alone, matching the
                      // file-manager convention the gesture borrows.
                      if (!isOn) setSelected(new Set([key]));
                      setMenu(menuPosition(e));
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 10px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--color-border-secondary)',
                      backgroundColor: isOn ? 'rgba(123, 150, 232,0.10)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isOn}
                      readOnly
                      tabIndex={-1}
                      style={{ width: '15px', height: '15px', pointerEvents: 'none' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontWeight: 600 }}>{r.label}</span>
                        <Pill label={r.targetType === 'WELL' ? 'Well' : 'Site'} tone="neutral" />
                      </div>
                      <div style={subText}>
                        {[r.customer, r.sub].filter((v) => v !== null).join(' · ')}
                      </div>
                    </div>
                    <div style={{ ...subText, textAlign: 'right', minWidth: '140px' }}>
                      <div>1: {s1?.name ?? '—'}</div>
                      <div>2: {s2?.name ?? '—'}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {targetNotice !== null && (
              <p style={{ ...subText, marginTop: '6px' }}>{targetNotice}</p>
            )}

            {/* A FULL page from the server is probably not the whole catalog. Saying so is the
                difference between a dispatcher narrowing the filter and one believing they
                just selected every one of a customer's wells. */}
            {wellsTruncated && matching.length > 0 && (
              <p
                style={{
                  ...subText,
                  marginTop: '6px',
                  color: 'var(--color-warning)',
                }}
              >
                Showing the first {WELL_SEARCH_MAX_LIMIT} matching wells — there may be more. Type
                in the filter to narrow before selecting.
              </p>
            )}
          </div>

          {/* ── Requestors: filter → pick one → act ──
              A card per person with its own pair of buttons does not survive hundreds of
              requestors, and neither does a context menu listing two entries each. Picking
              ONE active person collapses both problems: the slot controls and the menu are
              constant size no matter how many people exist. */}
          <div style={{ width: '250px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <input
              value={requestorNeedle}
              onChange={(e): void => setRequestorNeedle(e.target.value)}
              placeholder="Filter requestors…"
              // Distinct from the Requestors tab's own "Filter requestors" box, which stays
              // in the DOM behind this modal — two inputs sharing one accessible name gives a
              // screen reader no way to tell them apart, and it makes "the requestor filter"
              // ambiguous to any automation too.
              aria-label="Filter requestors in bulk assignment"
              style={{ ...controlStyle, marginBottom: '6px' }}
            />

            {requestors.length === 0 && <p style={subText}>No active requestors yet.</p>}

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '32vh',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '4px',
              }}
            >
              {requestors.length > 0 && cappedRequestors.total === 0 && (
                <p style={{ ...subText, padding: '8px' }}>No requestors match.</p>
              )}
              {cappedRequestors.shown.map((r) => {
                const isActive = r.id === activeRequestorId;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={(): void => setActiveRequestorId(isActive ? null : r.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: '13px',
                      background: isActive ? 'rgba(123, 150, 232,0.14)' : 'none',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border-secondary)',
                      color: 'var(--color-text-primary)',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontWeight: isActive ? 700 : 600 }}>{r.name}</span>
                    {/* Company is the only thing distinguishing two people with the same
                        name, which at hundreds across many operators is not hypothetical. */}
                    {r.company !== null && <div style={subText}>{r.company}</div>}
                  </button>
                );
              })}
            </div>
            {requestorNotice !== null && (
              <p style={{ ...subText, marginTop: '4px' }}>{requestorNotice}</p>
            )}

            {/* The slot controls act on the picked person and are also the drop targets. */}
            <div style={{ marginTop: '10px' }}>
              {activeRequestor === null ? (
                <p style={subText}>Pick a requestor above to assign or drag onto.</p>
              ) : (
                <>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                    Assign {activeRequestor.name} to:
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {dropZone(activeRequestor.id, 1)}
                    {dropZone(activeRequestor.id, 2)}
                  </div>
                  <p style={{ ...subText, marginTop: '6px' }}>
                    {formatNumber(selectedCount)} selected · drag them onto a slot, or right-click
                    them.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right-click menu. The two Clear actions need no requestor; the assign actions are
            listed per person so the gesture stays one step. */}
        {menu !== null && (
          <div
            role="menu"
            style={{
              position: 'fixed',
              left: `${menu.x}px`,
              top: `${menu.y}px`,
              zIndex: 60,
              minWidth: '210px',
              maxHeight: '260px',
              overflowY: 'auto',
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: '6px',
              padding: '4px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            }}
            onClick={(e): void => e.stopPropagation()}
          >
            <div style={{ ...subText, padding: '4px 8px' }}>
              {formatNumber(selectedCount)} selected
            </div>
            {/* Four items, always — the menu acts on the person picked in the rail rather
                than listing everyone. A menu cannot hold a search box, so at hundreds of
                requestors the old two-entries-per-person version was unusable. */}
            {activeRequestor === null ? (
              <div style={{ ...subText, padding: '5px 8px' }}>
                Pick a requestor in the panel to assign.
              </div>
            ) : (
              ([1, 2] as const).map((slot) => (
                <button
                  key={`set-${slot}`}
                  type="button"
                  role="menuitem"
                  onClick={(): void =>
                    request({ kind: 'set', slot, requestorId: activeRequestor.id })
                  }
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '5px 8px',
                    fontSize: '13px',
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  Assign {activeRequestor.name} as requestor {slot}
                </button>
              ))
            )}
            <div
              style={{
                borderTop: '1px solid var(--color-border-secondary)',
                margin: '4px 0',
              }}
            />
            {([1, 2] as const).map((slot) => (
              <button
                key={`clear-${slot}`}
                type="button"
                role="menuitem"
                onClick={(): void => request({ kind: 'clear', slot })}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '5px 8px',
                  fontSize: '13px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-danger)',
                  cursor: 'pointer',
                }}
              >
                Clear requestor {slot}
              </button>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>

        <ConfirmDialog
          prompt={pendingPrompt}
          pending={bulk.isPending}
          onCancel={(): void => setPendingChange(null)}
          onConfirm={(): void => {
            if (pendingChange !== null) commit(pendingChange);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
